/**
 * Premium bundles (`premium_bundle`) and sticker packs (`sticker_pack`).
 *
 * A premium bundle is ONE purchasable Wallet Shop product that grants up to six existing products,
 * one per slot: a profile frame, a profile background, a chat theme, a sticker pack, an entry
 * animation and a profile animation. The wallet stores references only (`BundleItems`), never copies of the components.
 * A sticker pack groups existing `sticker` products (`StickerProductKeys`).
 * Both are shown in the app under Wallet Shop → Themes & Decor.
 *
 * These rules mirror tezda-wallet's server-side validation so the form fails fast; the server
 * re-checks everything:
 *   - BundleItems: max 6, at most one item per slot, allowed component Types only.
 *   - Activation (IsActive: true): every Required item exists in the same Environment, has the
 *     declared Type and has media. Inactive drafts may be saved incomplete.
 *   - Price: exactly one positive price (coin → PriceCoins > 0, token → PriceTokens > 0) when active.
 *   - Cover: a WebP `thumbnail` MediaAsset (optional `image`).
 */

export const PREMIUM_BUNDLE_TYPE = 'premium_bundle';
export const STICKER_PACK_TYPE = 'sticker_pack';
export const STICKER_TYPE = 'sticker';
export const THEMES_DECOR_CATEGORY = 'THEMES_DECOR';
export const MAX_BUNDLE_ITEMS = 6;
export const STICKER_PACK_LIMITS = { min: 1, max: 50 };

export type ThemePreference = 'adaptive' | 'light' | 'dark';
export type CurrencyType = 'coin' | 'token';
export type Environment = 'dev' | 'prod';

export type ShopMediaAsset = {
  Type: string;
  Url: string;
  MimeType?: string;
  Width?: number;
  Height?: number;
  ThumbnailUrl?: string;
};

export type BundleItem = { ProductKey: string; Type: string; DisplayOrder: number; Required: boolean };

/** BundleItems as `getWalletShopProductsV2` returns them for admin reads. */
export type ResolvedBundleItem = BundleItem & { Name?: string; MediaAssets?: ShopMediaAsset[]; IsMissing?: boolean };

/** An existing product that can be picked as a bundle component or pack member. */
export type CandidateProduct = {
  ProductKey: string;
  Type: string;
  Name?: string;
  IsActive?: boolean;
  Environment?: Environment;
  MediaAssets?: ShopMediaAsset[];
};

export type BundleSlotKey = 'frame' | 'background' | 'chat_theme' | 'sticker_pack' | 'entry_animation' | 'profile_animation';
export type BundleSlot = { key: BundleSlotKey; label: string; types: string[] };

export const BUNDLE_SLOTS: BundleSlot[] = [
  { key: 'frame', label: 'Profile frame', types: ['profile_frame', 'profile_frame_animated'] },
  { key: 'background', label: 'Profile background', types: ['background', 'background_animated'] },
  { key: 'chat_theme', label: 'Chat theme', types: ['chat_themes'] },
  { key: 'sticker_pack', label: 'Sticker pack', types: [STICKER_PACK_TYPE] },
  { key: 'entry_animation', label: 'Entry animation', types: ['entry_animation'] },
  // Applying the bundle equips UserProfile.ProfileAnimation (webm / ios_alpha video, optional thumbnail).
  { key: 'profile_animation', label: 'Profile animation', types: ['profile_animation'] },
];

export const BUNDLE_COMPONENT_TYPES = BUNDLE_SLOTS.flatMap((slot) => slot.types);

export const PRODUCT_TYPE_LABELS: Record<string, string> = {
  profile_frame: 'Profile Frame',
  profile_frame_animated: 'Animated Profile Frame',
  background: 'Background',
  background_animated: 'Animated Background',
  chat_themes: 'Chat Theme',
  sticker_pack: 'Sticker Pack',
  entry_animation: 'Entry Animation',
  profile_animation: 'Profile Animation',
  sticker: 'Sticker',
  premium_bundle: 'Premium Bundle',
};
export const productTypeLabel = (type: string) => PRODUCT_TYPE_LABELS[type] || type;

/**
 * CategoryKey sent with the per-Type candidate query. These are the category/type pairs the rest of
 * the dashboard already loads through getWalletShopProductsV2; `undefined` means query by Type only
 * (new types, whose category the backend owns).
 */
export const CANDIDATE_CATEGORY_BY_TYPE: Record<string, string | undefined> = {
  profile_frame: 'PROFILE',
  profile_frame_animated: 'PROFILE',
  background: 'PROFILE',
  background_animated: 'PROFILE',
  chat_themes: 'PROFILE',
  entry_animation: 'LIVE',
  profile_animation: 'PROFILE',
  sticker: 'STICKER',
  sticker_pack: undefined,
  premium_bundle: undefined,
};

export const slotForType = (type: string): BundleSlot | undefined => BUNDLE_SLOTS.find((slot) => slot.types.includes(type));
export const slotByKey = (key: BundleSlotKey): BundleSlot => BUNDLE_SLOTS.find((slot) => slot.key === key) as BundleSlot;

export const hasMedia = (assets?: ShopMediaAsset[]) => !!assets?.some((asset) => !!asset.Url && !!asset.Url.trim());

const toLookup = (candidates: Map<string, CandidateProduct> | CandidateProduct[]) =>
  candidates instanceof Map ? candidates : new Map(candidates.map((candidate) => [candidate.ProductKey, candidate]));

// ───────────────────────────── Bundle items ─────────────────────────────

/** DisplayOrder follows list position (0-based), which is what the up/down buttons change. */
export const normalizeBundleItems = (items: BundleItem[]): BundleItem[] =>
  items.map((item, index) => ({
    ProductKey: item.ProductKey.trim(),
    Type: item.Type,
    DisplayOrder: index,
    Required: !!item.Required,
  }));

/** Reads BundleItems from an API product (resolved or not), sorted by DisplayOrder, stripped to the upsert shape. */
export const bundleItemsFromProduct = (product: { BundleItems?: ResolvedBundleItem[] } | null | undefined): BundleItem[] =>
  normalizeBundleItems(
    [...(product?.BundleItems || [])]
      .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))
      .map((item) => ({
        ProductKey: item.ProductKey || '',
        Type: item.Type,
        DisplayOrder: item.DisplayOrder ?? 0,
        Required: item.Required !== false,
      }))
  );

/**
 * Puts a picked product in its slot. A slot that already has a component is replaced in place
 * (keeping its position and Required flag); otherwise the component is appended as Required.
 */
export const upsertSlotItem = (items: BundleItem[], candidate: { ProductKey: string; Type: string }): BundleItem[] => {
  const slot = slotForType(candidate.Type);
  if (!slot) throw new Error(`"${candidate.Type}" products can't be added to a premium bundle.`);
  const index = items.findIndex((item) => slotForType(item.Type)?.key === slot.key);
  if (index >= 0) {
    const next = [...items];
    next[index] = { ...next[index], ProductKey: candidate.ProductKey, Type: candidate.Type };
    return normalizeBundleItems(next);
  }
  if (items.length >= MAX_BUNDLE_ITEMS) throw new Error(`A premium bundle can hold at most ${MAX_BUNDLE_ITEMS} components.`);
  return normalizeBundleItems([...items, { ProductKey: candidate.ProductKey, Type: candidate.Type, DisplayOrder: items.length, Required: true }]);
};

export const removeBundleItem = (items: BundleItem[], index: number) => normalizeBundleItems(items.filter((_, i) => i !== index));

export const moveBundleItem = (items: BundleItem[], index: number, delta: -1 | 1): BundleItem[] => {
  const target = index + delta;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return normalizeBundleItems(next);
};

const itemLabel = (item: { ProductKey: string; Type: string }) =>
  `${slotForType(item.Type)?.label || productTypeLabel(item.Type)} "${item.ProductKey}"`;

/** Rules every save must pass, drafts included. */
export function bundleStructureIssues(items: BundleItem[]): string[] {
  const issues: string[] = [];
  if (items.length > MAX_BUNDLE_ITEMS) {
    issues.push(`A premium bundle can hold at most ${MAX_BUNDLE_ITEMS} components (this one has ${items.length}).`);
  }
  const seenSlots = new Map<BundleSlotKey, BundleItem>();
  const seenKeys = new Set<string>();
  items.forEach((item, index) => {
    const key = (item.ProductKey || '').trim();
    if (!key) {
      issues.push(`Component ${index + 1} has no product selected.`);
      return;
    }
    const slot = slotForType(item.Type);
    if (!slot) {
      issues.push(
        `"${key}" has Type "${item.Type}", which can't be part of a bundle (allowed: ${BUNDLE_COMPONENT_TYPES.join(', ')}).`
      );
      return;
    }
    const previous = seenSlots.get(slot.key);
    if (previous) issues.push(`Only one ${slot.label.toLowerCase()} per bundle ("${previous.ProductKey}" and "${key}").`);
    else seenSlots.set(slot.key, item);
    if (seenKeys.has(key)) issues.push(`"${key}" is added more than once.`);
    seenKeys.add(key);
    if (!Number.isInteger(item.DisplayOrder) || item.DisplayOrder < 0) {
      issues.push(`${itemLabel(item)} needs a DisplayOrder of 0 or more.`);
    }
  });
  return issues;
}

/**
 * Extra rules for IsActive: true, mirroring the server's activation check. `candidates` are the
 * existing products loaded for the bundle's Environment (inactive ones included — bundle-exclusive
 * components that aren't sold separately are allowed).
 */
export function bundleActivationIssues(
  items: BundleItem[],
  candidates: Map<string, CandidateProduct> | CandidateProduct[],
  environment: Environment
): string[] {
  if (!items.length) return ['Add at least one component before activating the bundle.'];
  const lookup = toLookup(candidates);
  const issues: string[] = [];
  items.forEach((item) => {
    if (!item.Required || !item.ProductKey.trim()) return;
    const product = lookup.get(item.ProductKey.trim());
    if (!product) {
      issues.push(`${itemLabel(item)} doesn't exist in ${environment}.`);
      return;
    }
    if (product.Type !== item.Type) {
      issues.push(`${itemLabel(item)} is a ${productTypeLabel(product.Type)}, not a ${productTypeLabel(item.Type)}.`);
      return;
    }
    if (!hasMedia(product.MediaAssets)) issues.push(`${itemLabel(item)} has no media.`);
  });
  return issues;
}

// ───────────────────────────── Cover media ─────────────────────────────

const MB = 1024 * 1024;
export const COVER_SLOTS = {
  thumbnail: {
    label: 'Cover thumbnail',
    accept: 'image/webp,.webp',
    mimes: ['image/webp'],
    exts: ['webp'],
    maxBytes: 1 * MB,
    hint: 'WebP, 1 MB max — required. Shown on the shop card.',
  },
  image: {
    label: 'Cover image',
    accept: 'image/png,image/jpeg,image/webp',
    mimes: ['image/png', 'image/jpeg', 'image/webp'],
    exts: ['png', 'jpg', 'jpeg', 'webp'],
    maxBytes: 5 * MB,
    hint: 'PNG, JPG or WebP, 5 MB max — optional larger artwork.',
  },
} as const;
export type CoverSlotType = keyof typeof COVER_SLOTS;
export const COVER_SLOT_TYPES = Object.keys(COVER_SLOTS) as CoverSlotType[];

const mb = (bytes: number) => `${(bytes / MB).toFixed(bytes < MB ? 2 : 0)} MB`;
const extOf = (name: string) => name.split(/[?#]/)[0].split('.').pop()?.toLowerCase() || '';

/** Null when the file can go in the slot, otherwise the message to show. */
export function checkCoverFile(slotType: CoverSlotType, file: { name: string; type: string; size: number }): string | null {
  const slot = COVER_SLOTS[slotType];
  if (!(slot.exts as readonly string[]).includes(extOf(file.name))) {
    return `${slot.label} must be a ${slot.exts.map((ext) => `.${ext}`).join(' / ')} file (got ${file.name}).`;
  }
  if (file.type && !(slot.mimes as readonly string[]).includes(file.type)) {
    return `${slot.label} must be ${slot.mimes.join(' or ')} (got ${file.type}).`;
  }
  if (file.size > slot.maxBytes) return `${file.name} is ${mb(file.size)}; the limit for the ${slot.label.toLowerCase()} is ${mb(slot.maxBytes)}.`;
  return null;
}

export function coverIssues(mediaAssets: ShopMediaAsset[], isActive: boolean): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  mediaAssets.forEach((asset) => {
    if (!(COVER_SLOT_TYPES as string[]).includes(asset.Type)) {
      issues.push(`Media slot "${asset.Type}" isn't used by this product type (only thumbnail and image).`);
      return;
    }
    if (seen.has(asset.Type)) issues.push(`Only one ${COVER_SLOTS[asset.Type as CoverSlotType].label.toLowerCase()} is allowed.`);
    seen.add(asset.Type);
    if (!/^https:\/\//i.test(asset.Url || '')) issues.push(`${COVER_SLOTS[asset.Type as CoverSlotType].label} URL must use HTTPS.`);
  });
  const thumbnail = mediaAssets.find((asset) => asset.Type === 'thumbnail');
  if (thumbnail && extOf(thumbnail.Url) !== 'webp') issues.push('Cover thumbnail must be a .webp file.');
  if (!thumbnail && isActive) issues.push('Upload a WebP cover thumbnail before activating.');
  return issues;
}

/** Thumbnail first: clients that only read MediaAssets[0] show the cover. */
export const orderCoverMediaAssets = (assets: ShopMediaAsset[]) => [
  ...assets.filter((asset) => asset.Type === 'thumbnail'),
  ...assets.filter((asset) => asset.Type !== 'thumbnail'),
];

// ───────────────────────────── Shared product fields ─────────────────────────────

export type ShopProductForm = {
  ProductKey: string;
  Name: string;
  Description: string;
  CategoryKey: string;
  DisplayOrder: string;
  Tags: string;
  IsFeatured: boolean;
  Environment: Environment;
  AvailabilityType: 'unlimited' | 'limited';
  AvailabilityStartAt: string;
  AvailabilityEndAt: string;
  QuantityAvailable: string;
  PerUserLimit: string;
  MaxPurchasePerOrder: string;
  IsActive: boolean;
  /** '' = not set: each bundle component keeps its own Theme Preference. */
  ThemePreference: '' | ThemePreference;
  CurrencyType: CurrencyType;
  PriceCoins: string;
  PriceTokens: string;
};

export const emptyShopProductForm = (environment: Environment = 'prod'): ShopProductForm => ({
  ProductKey: '',
  Name: '',
  Description: '',
  CategoryKey: THEMES_DECOR_CATEGORY,
  DisplayOrder: '',
  Tags: '',
  IsFeatured: false,
  Environment: environment,
  AvailabilityType: 'unlimited',
  AvailabilityStartAt: '',
  AvailabilityEndAt: '',
  QuantityAvailable: '',
  PerUserLimit: '',
  MaxPurchasePerOrder: '',
  // New bundles start as drafts: they can be saved before every component exists.
  IsActive: false,
  ThemePreference: '',
  CurrencyType: 'coin',
  PriceCoins: '',
  PriceTokens: '',
});

const toDateInput = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // <input type="datetime-local"> is in local time; shift so the stored instant round-trips.
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/** Fills the form from an API product (used in edit mode). */
export const formFromProduct = (product: Record<string, any>, fallbackEnv: Environment = 'prod'): ShopProductForm => {
  const currency: CurrencyType = product.CurrencyType === 'token' ? 'token' : 'coin';
  const num = (value: unknown) => (value === undefined || value === null ? '' : String(value));
  return {
    ProductKey: product.ProductKey || '',
    Name: product.Name || '',
    Description: product.Description || '',
    CategoryKey: product.CategoryKey || THEMES_DECOR_CATEGORY,
    DisplayOrder: num(product.DisplayOrder),
    Tags: Array.isArray(product.Tags) ? product.Tags.join(', ') : '',
    IsFeatured: !!product.IsFeatured,
    Environment: product.Environment === 'dev' ? 'dev' : product.Environment === 'prod' ? 'prod' : fallbackEnv,
    AvailabilityType: product.AvailabilityType === 'limited' ? 'limited' : 'unlimited',
    AvailabilityStartAt: toDateInput(product.AvailabilityStartAt),
    AvailabilityEndAt: toDateInput(product.AvailabilityEndAt),
    QuantityAvailable: num(product.QuantityAvailable),
    PerUserLimit: num(product.PerUserLimit),
    MaxPurchasePerOrder: num(product.MaxPurchasePerOrder),
    IsActive: product.IsActive !== false,
    ThemePreference: ['adaptive', 'light', 'dark'].includes(product.ThemePreference) ? product.ThemePreference : '',
    CurrencyType: currency,
    PriceCoins: currency === 'coin' ? num(product.PriceCoins) : '',
    PriceTokens: currency === 'token' ? num(product.PriceTokens) : '',
  };
};

export const normalizeProductKey = (value: string) => value.trim().replace(/\s+/g, '_').toUpperCase();

type BuildResult = { payload: Record<string, any>; issues: string[] };

function buildCommonPayload(form: ShopProductForm, type: string, mediaAssets: ShopMediaAsset[]): BuildResult {
  const issues: string[] = [];
  const productKey = normalizeProductKey(form.ProductKey);
  if (!productKey) issues.push('ProductKey is required.');
  if (!form.Name.trim()) issues.push('Name is required.');
  if (!form.CategoryKey.trim()) issues.push('CategoryKey is required.');

  const number = (raw: string, label: string, { integer = true, min = 0 } = {}) => {
    if (!raw.trim()) return undefined;
    const value = Number(raw);
    if (Number.isNaN(value) || (integer && !Number.isInteger(value)) || value < min) {
      issues.push(`${label} must be a whole number of ${min} or more.`);
      return undefined;
    }
    return value;
  };

  const priceCoins = form.CurrencyType === 'coin' ? number(form.PriceCoins, 'Price (coins)') ?? 0 : 0;
  const priceTokens = form.CurrencyType === 'token' ? number(form.PriceTokens, 'Price (tokens)') ?? 0 : 0;
  if (form.IsActive) {
    if (form.CurrencyType === 'coin' && !(priceCoins > 0)) issues.push('Active products need a coin price greater than 0.');
    if (form.CurrencyType === 'token' && !(priceTokens > 0)) issues.push('Active products need a token price greater than 0.');
  }

  const payload: Record<string, any> = {
    ProductKey: productKey,
    Name: form.Name.trim(),
    CategoryKey: form.CategoryKey.trim(),
    Type: type,
    AvailabilityType: form.AvailabilityType,
    // Exactly one currency: the other price is sent as 0 so switching currency on edit passes the
    // server's single-currency check.
    CurrencyType: form.CurrencyType,
    PriceCoins: priceCoins,
    PriceTokens: priceTokens,
    IsActive: form.IsActive,
    IsFeatured: form.IsFeatured,
    Environment: form.Environment,
    Tags: form.Tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    MediaAssets: orderCoverMediaAssets(mediaAssets),
  };

  const description = form.Description.trim();
  if (description) payload.Description = description;
  if (form.ThemePreference) payload.ThemePreference = form.ThemePreference;

  const displayOrder = number(form.DisplayOrder, 'DisplayOrder');
  if (displayOrder !== undefined) payload.DisplayOrder = displayOrder;
  const quantity = number(form.QuantityAvailable, 'Quantity available');
  if (form.AvailabilityType === 'limited' && quantity === undefined) issues.push('Quantity available is required for limited availability.');
  if (quantity !== undefined) payload.QuantityAvailable = quantity;
  const perUserLimit = number(form.PerUserLimit, 'Per-user limit', { min: 1 });
  if (perUserLimit !== undefined) payload.PerUserLimit = perUserLimit;
  const maxPerOrder = number(form.MaxPurchasePerOrder, 'Max purchase per order', { min: 1 });
  if (maxPerOrder !== undefined) payload.MaxPurchasePerOrder = maxPerOrder;

  const date = (raw: string, label: string) => {
    if (!raw.trim()) return undefined;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      issues.push(`${label} is not a valid date.`);
      return undefined;
    }
    return parsed.toISOString();
  };
  const startAt = date(form.AvailabilityStartAt, 'Availability start');
  const endAt = date(form.AvailabilityEndAt, 'Availability end');
  if (startAt) payload.AvailabilityStartAt = startAt;
  if (endAt) payload.AvailabilityEndAt = endAt;
  if (startAt && endAt && endAt <= startAt) issues.push('Availability end must be after availability start.');

  issues.push(...coverIssues(mediaAssets, form.IsActive));
  return { payload, issues };
}

/**
 * Builds the createWalletShopProduct / updateWalletShopProduct body for a premium bundle and lists
 * everything that blocks saving. Drafts (IsActive false) only need the structural rules; active
 * bundles must also pass the server's activation rules against `candidates`.
 */
export function buildPremiumBundlePayload(
  form: ShopProductForm,
  items: BundleItem[],
  mediaAssets: ShopMediaAsset[],
  candidates: Map<string, CandidateProduct> | CandidateProduct[]
): BuildResult {
  const { payload, issues } = buildCommonPayload(form, PREMIUM_BUNDLE_TYPE, mediaAssets);
  const bundleItems = normalizeBundleItems(items);
  issues.push(...bundleStructureIssues(bundleItems));
  if (form.IsActive) issues.push(...bundleActivationIssues(bundleItems, candidates, form.Environment));
  payload.BundleItems = bundleItems;
  return { payload, issues };
}

/** Builds the upsert body for a sticker pack of existing `sticker` products. */
export function buildStickerPackPayload(
  form: ShopProductForm,
  stickerProductKeys: string[],
  mediaAssets: ShopMediaAsset[],
  stickers?: Map<string, CandidateProduct> | CandidateProduct[]
): BuildResult {
  const { payload, issues } = buildCommonPayload(form, STICKER_PACK_TYPE, mediaAssets);
  const keys = Array.from(new Set(stickerProductKeys.map((key) => key.trim()).filter(Boolean)));
  const { min, max } = STICKER_PACK_LIMITS;
  if (keys.length < min || keys.length > max) {
    issues.push(`A sticker pack must contain ${min} to ${max} stickers (this one has ${keys.length}).`);
  }
  if (stickers && form.IsActive) {
    const lookup = toLookup(stickers);
    keys.forEach((key) => {
      const sticker = lookup.get(key);
      if (!sticker) issues.push(`Sticker "${key}" doesn't exist in ${form.Environment}.`);
      else if (sticker.Type && sticker.Type !== STICKER_TYPE) issues.push(`"${key}" is a ${productTypeLabel(sticker.Type)}, not a sticker.`);
    });
  }
  payload.StickerProductKeys = keys;
  return { payload, issues };
}

// ───────────────────────────── Preview ─────────────────────────────

const isHex = (value: string) => /^#[0-9a-f]{3,8}$/i.test((value || '').trim());
const IMAGE_PRIORITY = [
  'thumbnail',
  'image',
  'thumbnail_light_mode',
  'thumbnail_dark_mode',
  'theme_light_mode',
  'theme_dark_mode',
  'light_mode',
  'dark_mode',
];
const VIDEO_PRIORITY = ['webm', 'ios_alpha'];

export type PreviewMedia = { kind: 'image' | 'video' | 'color'; url: string; type: string };

/** The still to show for a product: an image asset, or a hex colour for solid backgrounds. */
export function pickPreviewImage(assets: ShopMediaAsset[] = []): PreviewMedia | null {
  for (const type of IMAGE_PRIORITY) {
    const asset = assets.find((a) => a.Type === type && a.Url && a.Url.trim());
    if (!asset) continue;
    if (isHex(asset.Url)) return { kind: 'color', url: asset.Url.trim(), type };
    if (/^https?:\/\//i.test(asset.Url)) return { kind: 'image', url: asset.Url, type };
  }
  const anyImage = assets.find((a) => /\.(png|jpe?g|webp|gif|svg)(\?|#|$)/i.test(a.Url || ''));
  return anyImage ? { kind: 'image', url: anyImage.Url, type: anyImage.Type } : null;
}

/** The animated asset to play (webm first; Chrome can't decode HEVC-alpha .mov). */
export function pickPreviewVideo(assets: ShopMediaAsset[] = []): PreviewMedia | null {
  for (const type of VIDEO_PRIORITY) {
    const asset = assets.find((a) => a.Type === type && /^https?:\/\//i.test(a.Url || ''));
    if (asset) return { kind: 'video', url: asset.Url, type };
  }
  return null;
}
